import * as _ from 'lodash';
import { UtilitiesService } from 'src/ng2/shared/services/utilities/utilities.service';

interface IMissingAttDates {
  eventDate: string;
  inactive?: boolean;
  isUnlogged?: boolean;
}

/**
 * This service encapsulates all the shared functionality across the different
 * support attendance views. E.g. my-supports, support-overview, support-attendance
 */
export class SupportAttendanceService {
  static $inject = ['UtilitiesService', 'Rollbar'];

  constructor(private utils: UtilitiesService, private Rollbar) {}

  /**
   * Summarize attendance records by a field on the record, E.g. 'eventDate', 'studentId';
   * Create a an object like:
   *    {
   *      [isoDate]: {
   *         ABSENT: number
   *         LATE: number
   *         ABSENT: number
   *         total: number
   *         daysAbsent?: string[]
   *         studentsAbsent?: string[]
   *      }
   *    }
   */
  summarizeAttendanceData(attendanceRecords, summarizeBy) {
    // Group records by the provided summarizeBy key
    const groupedRecords = _.groupBy(attendanceRecords, summarizeBy);

    // Loop over each and tally absent, present, late and total
    const summarizedRecords = _.map(groupedRecords, (records, summarizeByVal) => {
      const reduced = _.reduce(
        records,
        (a: any, record: any) => {
          if (!a[summarizeBy]) a[summarizeBy] = summarizeByVal;
          if (!a.supportId) a.supportId = record.supportId;
          a.studentId = record.studentId;
          a = this._makeAnyNeededJoins(summarizeBy, record, a);
          a[record.status]++;
          a.total++;
          return a;
        },
        {
          ABSENT: 0,
          LATE: 0,
          PRESENT: 0,
          total: 0,
        },
      );

      return reduced;
    });

    // Calculate percentages for absent, present, late
    _.forEach(summarizedRecords, record => {
      const total = record.total;
      record.percentAbs = Math.round((record.ABSENT / total) * 100);
      record.percentLate = Math.round((record.LATE / total) * 100);
      record.percentPres = Math.round((record.PRESENT / total) * 100);

      const presAndLate = record.PRESENT + record.LATE;
      record.percentPresAndLate = Math.round((presAndLate / total) * 100);
      return record;
    });

    return summarizedRecords;
  }

  _makeAnyNeededJoins(summarizeBy, record, _accumulator) {
    const accumulator = _.cloneDeep(_accumulator);
    if (summarizeBy === 'studentId') {
      if (!accumulator.daysAbsent) accumulator.daysAbsent = [];
      if (record.status === 'ABSENT') {
        const human = this.utils.convertISODateToHumanDate(record.eventDate);
        accumulator.daysAbsent.push(human);
      }
    }

    if (summarizeBy === 'eventDate') {
      if (!accumulator.studentsAbsent) accumulator.studentsAbsent = [];
      if (record.status === 'ABSENT') {
        // TODO: if student name gets appended to attendanceRecord schema, push name instead of ID here
        accumulator.studentsAbsent.push(record.studentId);
      }
    }
    return accumulator;
  }

  /**
   * Utility to compare given date to today's date
   */
  _isAfterToday(date) {
    return moment(date).isAfter(moment());
  }

  /**
   * Gets all attendance event dates that recur within a given support's start/end dates
   */
  getAllAttendanceDates(support) {
    const startsOn = support.schedule.startsOn;
    if (this._isAfterToday(startsOn)) return [];
    const end = this._getMaxDate(support);
    const schedule = support.schedule;
    const repeats = schedule.repeats;
    const repeatsBy = schedule.repeatsBy;
    const every = schedule.repeatsEvery;

    // schedule.repeatsEvery should never be 0 or else it will cause infinite loop/app crash
    if (!every) {
      this.Rollbar.warning(`schedule.repeatsEvery cannot be 0. [Support._id ${support._id}]`);
      return [];
    }
    const holidays = this._getSchoolHolidays();
    if (repeats === 'NONE') {
      return [moment(startsOn)];
    }
    if (repeats === 'DAILY') {
      return moment()
        .recur({
          start: startsOn,
          end,
          exceptions: holidays,
        })
        .every(every)
        .days()
        .all();
    }

    if (repeats === 'WEEKLY') {
      // Note: different format than other repeat options.
      // No good reason.Couldn't figure it out :)
      const repeatsOn = this._repeatMapper(schedule.repeatsOn);
      const rules = [
        {
          units: repeatsOn,
          measure: 'daysOfWeek',
        },
      ];
      const all = moment()
        .recur({
          start: startsOn,
          end,
          rules,
          exceptions: holidays,
        })
        .all();
      const filtered = [];
      let i = 0;
      for (i; i < all.length; i += every) {
        filtered.push(all[i]);
      }
      return filtered;
    }

    if (repeats === 'MONTHLY') {
      if (repeatsBy === 'DAY_OF_MONTH') {
        return moment()
          .recur({
            start: startsOn,
            end,
            exceptions: holidays,
          })
          .every(every)
          .months()
          .all();
      }
      if (repeatsBy === 'DAY_OF_WEEK') {
        const m = moment(startsOn);
        const dayOfWeek = m.weekday();
        const weekOfMonth = Math.ceil(m.date() / 7);
        return moment
          .recur({
            start: startsOn,
            end,
            exceptions: holidays,
          })
          .every(dayOfWeek)
          .daysOfWeek()
          .every(weekOfMonth)
          .weeksOfMonthByDay()
          .all();
      }
    }
    /* istanbul ignore next */
    this.Rollbar.warning(`WARNING: invalid repeats value of ${repeats}`);
  }

  // return date for which attendance isn't logged and dates which doesn't have any active students (i.e.: no
  // student has a support assigned on that date)
  getMissingAttendanceDates(support, attendanceRecords, studentSupports, includeToday = false): IMissingAttDates[] {
    const expectedDates = this.getAllAttendanceDates(support);

    // find min date in student supports
    const earliestSupportStartDate = _.min(
      studentSupports.map(result => {
        const { startsOn } = result;
        // force earliestSupportStartDate date format to be 10 digits
        // earliestSupportStartDate must be the same as eventDate format for comparison in returnUnlogged function below
        if (startsOn && startsOn.length > 10) return startsOn.slice(0, 10);
        return startsOn;
      }),
    );

    const missingDates: IMissingAttDates[] = expectedDates.reduce(function returnUnlogged(result, momnt) {
      const eventDate = momnt.format('YYYY-MM-DD');
      const found = _.find(attendanceRecords, { eventDate });
      const today = moment();
      const isToday = momnt.isSame(today, 'day');

      if (!found) {
        if (!isToday || includeToday) {
          const isInactive = !earliestSupportStartDate || eventDate < earliestSupportStartDate;
          if (isInactive) {
            result.push({ eventDate, inactive: true });
          } else {
            result.push({ eventDate, isUnlogged: true });
          }
        }
      }
      return result;
    }, []);
    return missingDates;
  }

  /**
   * Should return an array of school holidays to be excluded from expected dates
   */
  _getSchoolHolidays() {
    return [
      '2016-09-12',
      '2016-10-03',
      '2016-10-04',
      '2016-10-10',
      '2016-10-12',
      '2016-11-08',
      '2016-11-11',
      '2016-11-24',
      '2016-11-25',
      '2016-12-26',
      '2016-12-27',
      '2016-12-28',
      '2016-12-29',
      '2016-12-30',
      '2017-01-02',
      '2017-01-16',
      '2017-01-30',
      '2017-02-09',
      '2017-02-20',
      '2017-02-21',
      '2017-02-22',
      '2017-02-23',
      '2017-02-24',
      '2017-03-14',
      '2017-04-10',
      '2017-04-11',
      '2017-04-12',
      '2017-04-13',
      '2017-04-14',
      '2017-04-17',
      '2017-04-18',
      '2017-05-29',
      '2017-06-08',
      '2017-06-26',
      '2017-06-29',
      '2017-06-30',
    ];
  }

  /**
   * Returns the later of either the end date or todays' date
   */
  _getMaxDate(support) {
    const endDateString = support.schedule.endsOn;
    const end = moment(endDateString);
    const today = moment();
    if (end.isAfter(today)) return today;
    return end;
  }

  /**
   * Converts the existing object to an object expected by the moment-recur rule option
   */
  _repeatMapper(repeats) {
    const mapping = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };
    return _.reduce(
      repeats,
      function mapStrToNumDays(a, doesMeet, dayString) {
        const dayNum = mapping[dayString];
        a[dayNum] = doesMeet;
        return a;
      },
      {},
    );
  }
}
